import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment-timezone';

import Select from '../../../../components/Select';
import { Button, BUTTON_TYPE_WHITE } from '../../../../components/Buttons/Button';
import { notificationActions } from '../../../../components/Notification/redux/actions';
import Strings from '../../../../Strings';
import { DATE_TIME_SPACE_12_UPPERCASE } from '../../../../constants';
import { GET_ACCESS_SIM_COMM_LOG_RESULT, actions } from './actions';
import styles from './AccessSimulator.module.scss';

const bodyPreview = body => {
  if (!body) return '-';
  const oneLine = body.replace(/\s+/g, ' ').trim();
  return oneLine.length > 400 ? `${oneLine.slice(0, 400)}…` : oneLine;
};

// groupEntries folds the flat, chronological log into per-submission exchanges:
// every entry that carries a submission_id joins that submission's
// group (the initial request+response plus every poll), while entries without
// one (OAuth, injected email) stand alone. The flat API/DB is unchanged — this
// is purely a view.
const groupEntries = entries => {
  const groups = [];
  const bySubmission = new Map();
  entries.forEach(entry => {
    if (entry.submission_id) {
      if (!bySubmission.has(entry.submission_id)) {
        const group = { key: entry.submission_id, submission_id: entry.submission_id, entries: [] };
        bySubmission.set(entry.submission_id, group);
        groups.push(group);
      }
      bySubmission.get(entry.submission_id).entries.push(entry);
    } else {
      groups.push({ key: `standalone-${entry.id}`, submission_id: null, entries: [entry] });
    }
  });
  groups.forEach(group => group.entries.sort((a, b) => a.id - b.id));
  groups.sort((a, b) => a.entries[0].id - b.entries[0].id);
  return groups;
};

// The "primary" kind of an exchange: the operation that started it (the first
// non-poll entry), falling back to the first entry for poll-only groups.
const groupKind = group => group.entries.find(entry => entry.kind !== 'poll')?.kind || group.entries[0]?.kind;

/**
 * The us↔simulator communication log of one patient, rendered in
 * the expanded row of the patients table. Loaded lazily, when the row is
 * opened. Entries are grouped by submission into per-submission exchanges,
 * filterable by kind, with a copy-as-JSON action and expandable full bodies.
 */
function CommLog({ patient, orgUUID, getCommLog, showNotification }) {
  const [entries, setEntries] = React.useState(null);
  const [kindFilter, setKindFilter] = React.useState('all');
  const [expanded, setExpanded] = React.useState(() => new Set());

  React.useEffect(() => {
    let cancelled = false;
    setKindFilter('all');
    setExpanded(new Set());
    getCommLog(patient.id, undefined, orgUUID).then(resp => {
      if (cancelled) return;
      // Anything other than the result action is a broken call - report it
      // instead of silently showing an empty log.
      if (resp.type !== GET_ACCESS_SIM_COMM_LOG_RESULT) {
        showNotification(
          resp?.response?.data?.standard_error?.message || Strings.errors.internalError,
          5000,
          true,
        );
        setEntries([]);
        return;
      }
      setEntries(resp.response?.entries || []);
    });
    return () => {
      cancelled = true;
    };
  }, [patient.id, orgUUID, getCommLog]);

  const toggleBody = id => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const groups = entries === null ? [] : groupEntries(entries);
  const distinctKinds = [...new Set(groups.map(groupKind))].sort();
  const kindOptions = [{ value: 'all', label: Strings.accessSimAllKinds }, ...distinctKinds.map(kind => ({ value: kind, label: kind }))];
  const filteredGroups = kindFilter === 'all' ? groups : groups.filter(group => groupKind(group) === kindFilter);

  // Copy the currently visible (kind-filtered) view, not the whole log.
  const copyAsJson = async () => {
    const visible = filteredGroups.flatMap(group => group.entries);
    try {
      await navigator.clipboard.writeText(JSON.stringify(visible, null, 2));
      showNotification(Strings.copiedToClipboard, 3000);
    } catch (error) {
      showNotification(Strings.errors.internalError, 5000, true);
    }
  };

  if (entries === null) return <div className={styles.commLogLoading}><div className="spinner" /></div>;
  if (entries.length === 0) return <div className={styles.commLog}>{Strings.accessSimNoCommLog}</div>;

  return (
    <div className={styles.commLog}>
      <div className={styles.commLogToolbar}>
        <Select
          id="access-simulator-comm-log-kind"
          className={styles.commLogKindSelect}
          label={Strings.accessSimKindFilter}
          data={kindOptions}
          value={kindFilter}
          onChange={option => setKindFilter(option?.value ?? 'all')}
        />
        <Button
          type="button"
          buttonType={BUTTON_TYPE_WHITE}
          onClick={copyAsJson}
          data-testid="button[copy-comm-log-json]"
        >
          {Strings.accessSimCopyAsJson}
        </Button>
      </div>
      {filteredGroups.length === 0 && (
        <div className={styles.commLogEmpty}>{Strings.accessSimNoCommLog}</div>
      )}
      {filteredGroups.map(group => (
        <div className={styles.commLogGroup} key={group.key}>
          <div className={styles.commLogGroupHeader}>
            <span className={styles.commLogSubmission}>
              {group.submission_id || Strings.accessSimNoSubmission}
            </span>
            <span className={styles.commLogCount}>{group.entries.length}</span>
          </div>
          {group.entries.map(entry => (
            <div className={styles.commLogEntry} key={entry.id}>
              <span className={styles.commLogKind}>{entry.kind}</span>
              <span className={styles.commLogStatus}>{entry.http_status ?? '-'}</span>
              <span className={styles.commLogTime}>
                {moment(entry.created_at).format(DATE_TIME_SPACE_12_UPPERCASE)}
              </span>
              <div className={styles.commLogBody}>
                {expanded.has(entry.id)
                  ? <pre className={styles.commLogBodyFull}>{entry.body || '-'}</pre>
                  : <span className={styles.commLogBodyPreview}>{bodyPreview(entry.body)}</span>}
                {entry.body && (
                  <Button
                    type="button"
                    buttonType={BUTTON_TYPE_WHITE}
                    className={styles.commLogBodyToggle}
                    onClick={() => toggleBody(entry.id)}
                    data-testid="button[expand-body]"
                  >
                    {expanded.has(entry.id) ? Strings.accessSimCollapseBody : Strings.accessSimExpandBody}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

CommLog.propTypes = {
  patient: PropTypes.object.isRequired,
  orgUUID: PropTypes.string,
  getCommLog: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  getCommLog: (id, limit, orgUUID) => dispatch(actions.getCommLog(id, limit, orgUUID)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
});

export default connect(null, mapDispatchToProps)(CommLog);
